import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Users,
  Building,
} from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  parseISO,
  isWithinInterval,
} from "date-fns";

interface TimeOffRequest {
  request: {
    id: number;
    employeeId: number;
    policyId: number;
    startDate: string;
    endDate: string;
    totalHours: string;
    status: string;
    reason?: string;
  };
  policy: {
    id: number;
    name: string;
    color?: string;
  } | null;
  employee: {
    id: number;
    firstName: string;
    lastName: string;
    department?: string;
  } | null;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function TeamCalendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [scope, setScope] = useState<"all" | "team">("all");
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const { data: requests, isLoading } = useQuery<TimeOffRequest[]>({
    queryKey: ["/api/time-off/requests", scope, "approved"],
    queryFn: async () => {
      const params = new URLSearchParams({ status: "approved" });
      if (scope === "team") params.set("scope", "team");
      const res = await fetch(`/api/time-off/requests?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch requests");
      return res.json();
    },
  });

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  const getRequestsForDay = (day: Date) => {
    if (!requests) return [];
    return requests.filter((req) => {
      const startDate = parseISO(req.request.startDate);
      const endDate = parseISO(req.request.endDate);
      return isWithinInterval(day, { start: startDate, end: endDate }) || 
             isSameDay(day, startDate) || 
             isSameDay(day, endDate);
    });
  };

  const selectedDayRequests = selectedDay ? getRequestsForDay(selectedDay) : [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link href="/time-off">
          <Button variant="ghost" size="sm">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Team Calendar</h1>
          <p className="text-gray-500">View scheduled time off across your team</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={scope === "team" ? "default" : "outline"}
            size="sm"
            onClick={() => setScope("team")}
            className="flex items-center gap-2"
          >
            <Users className="h-4 w-4" />
            My Team
          </Button>
          <Button
            variant={scope === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setScope("all")}
            className="flex items-center gap-2"
          >
            <Building className="h-4 w-4" />
            All Company
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-medium min-w-[150px] text-center">
            {format(currentMonth, "MMMM yyyy")}
          </span>
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
            Today
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-3">
          <CardContent className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-t-lg overflow-hidden">
                  {WEEKDAYS.map((day) => (
                    <div key={day} className="bg-gray-50 dark:bg-gray-800 py-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-b-lg overflow-hidden">
                  {calendarDays.map((day, idx) => {
                    const dayRequests = getRequestsForDay(day);
                    const isSelected = selectedDay && isSameDay(day, selectedDay);
                    const isCurrentMonth = isSameMonth(day, currentMonth);
                    
                    return (
                      <div
                        key={idx}
                        onClick={() => setSelectedDay(day)}
                        className={`
                          bg-white dark:bg-gray-900 min-h-[100px] p-2 cursor-pointer transition-colors
                          ${!isCurrentMonth ? "bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500" : ""}
                          ${isSelected ? "ring-2 ring-blue-500 ring-inset" : ""}
                          ${isToday(day) ? "bg-blue-50 dark:bg-blue-900/30" : ""}
                          hover:bg-gray-50 dark:hover:bg-gray-800
                        `}
                      >
                        <div className={`text-sm font-medium mb-1 ${isToday(day) ? "text-blue-600" : ""}`}>
                          {format(day, "d")}
                        </div>
                        <div className="space-y-1">
                          {dayRequests.slice(0, 3).map((req) => (
                            <div
                              key={req.request.id}
                              className="text-xs px-1 py-0.5 rounded truncate"
                              style={{
                                backgroundColor: `${req.policy?.color || "#6B7280"}20`,
                                color: req.policy?.color || "#6B7280",
                              }}
                            >
                              {req.employee?.firstName} {req.employee?.lastName?.[0]}.
                            </div>
                          ))}
                          {dayRequests.length > 3 && (
                            <div className="text-xs text-gray-500 px-1">
                              +{dayRequests.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">
              {selectedDay ? format(selectedDay, "EEEE, MMMM d, yyyy") : "Select a day"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDay ? (
              selectedDayRequests.length > 0 ? (
                <div className="space-y-3">
                  {selectedDayRequests.map((req) => (
                    <div key={req.request.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="font-medium">
                        {req.employee?.firstName} {req.employee?.lastName}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: req.policy?.color || "#6B7280" }}
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{req.policy?.name}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {format(parseISO(req.request.startDate), "MMM d")} - {format(parseISO(req.request.endDate), "MMM d")}
                      </div>
                      <div className="text-xs text-gray-500">
                        {req.request.totalHours} hours
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <CalendarIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm">No one out on this day</p>
                </div>
              )
            ) : (
              <div className="text-center py-6 text-gray-500">
                <CalendarIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                <p className="text-sm">Click on a day to see who's out</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {requests && Array.from(new Set(requests.map(r => r.policy?.id).filter(Boolean))).map(policyId => {
              const policy = requests.find(r => r.policy?.id === policyId)?.policy;
              if (!policy) return null;
              return (
                <div key={policyId} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: policy.color || "#6B7280" }}
                  />
                  <span className="text-sm">{policy.name}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
